import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Receipt, Copy, ArrowUpDown, ArrowUp, ArrowDown, Download, User, Phone, Mail, MapPin, X, Settings, Clock, CheckCircle, Loader2, Send } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = { da_pagare: 'Da Pagare', pagato: 'Pagato', scaduto: 'Scaduto', attivo: 'Attivo', cessato: 'Cessato' };
const paymentMethods = { bonifico: 'Bonifico', bonifico_droppoint: 'Bonifico DropPoint', carta: 'Carta', rid: 'RID', contanti: 'Contanti' };

const emptyBilling = {
  contract_id: '',
  client_id: '',
  amount: '',
  billing_date: '',
  due_date: '',
  status: 'da_pagare',
  payment_method: '',
  notes: ''
};

const TelecomBilling = () => {
  const [billings, setBillings] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBilling, setEditingBilling] = useState(null);
  const [formData, setFormData] = useState(emptyBilling);
  const [amountSort, setAmountSort] = useState('none');
  const [dateSort, setDateSort] = useState('none');
  const [dueDateSort, setDueDateSort] = useState('none');
  const [statusSort, setStatusSort] = useState('none');
  
  // Client details dialog
  const [clientDetailsOpen, setClientDetailsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(false);
  const [clientFormData, setClientFormData] = useState({});

  // Email config state
  const [emailConfigOpen, setEmailConfigOpen] = useState(false);
  const [emailConfig, setEmailConfig] = useState({
    enabled: false,
    sender_email: 'no-reply@jvservice.it',
    sender_name: 'JV Service - Ufficio Telefonia',
    daily_send_time: '09:00',
    days_before_expiry: 30,
    email_subject: 'Scadenza Contratto Telefonico - {operator} - JV Service',
    email_body: ''
  });
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => { fetchData(); fetchEmailConfig(); }, []);

  const fetchData = async () => {
    try {
      const [billingsRes, contractsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/telecom/billing`),
        axios.get(`${API}/telecom/contracts`),
        axios.get(`${API}/clients`)
      ]);
      setBillings(billingsRes.data);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...formData, 
        amount: formData.amount ? parseFloat(formData.amount) : 0 
      };
      if (editingBilling) {
        await axios.put(`${API}/telecom/billing/${editingBilling.id}`, payload);
        toast.success('Fattura aggiornata');
      } else {
        await axios.post(`${API}/telecom/billing`, payload);
        toast.success('Fattura creata');
      }
      setDialogOpen(false);
      setEditingBilling(null);
      setFormData(emptyBilling);
      setClientSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (billing) => {
    setEditingBilling(billing);
    setFormData({
      contract_id: billing.contract_id || '',
      client_id: billing.client_id || '',
      amount: billing.amount ? billing.amount.toString() : '',
      billing_date: billing.billing_date || '',
      due_date: billing.due_date || '',
      status: billing.status || 'da_pagare',
      payment_method: billing.payment_method || '',
      notes: billing.notes || ''
    });
    setDialogOpen(true);
  };

  const handleClone = (billing) => {
    setEditingBilling(null);
    setFormData({
      contract_id: billing.contract_id || '',
      client_id: billing.client_id || '',
      amount: billing.amount ? billing.amount.toString() : '',
      billing_date: '', // Reset date for new billing
      due_date: '',
      status: 'da_pagare',
      payment_method: billing.payment_method || '',
      notes: billing.notes || ''
    });
    setDialogOpen(true);
    toast.info('Fattura clonata - modifica i dati e salva');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa fattura?')) return;
    try {
      await axios.delete(`${API}/telecom/billing/${id}`);
      toast.success('Fattura eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Client details functions
  const openClientDetails = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setSelectedClient(client);
      setClientFormData({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        postal_code: client.postal_code || ''
      });
      setEditingClient(false);
      setClientDetailsOpen(true);
    }
  };

  const handleSaveClient = async () => {
    try {
      await axios.put(`${API}/clients/${selectedClient.id}`, clientFormData);
      toast.success('Cliente aggiornato');
      setEditingClient(false);
      fetchData();
      // Update selected client
      setSelectedClient(prev => ({ ...prev, ...clientFormData }));
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('it-IT') : '-';

  const getStatusBadge = (status) => {
    const classes = { 
      da_pagare: 'bg-amber-100 text-amber-700', 
      pagato: 'bg-emerald-100 text-emerald-700', 
      scaduto: 'bg-red-100 text-red-700',
      attivo: 'bg-blue-100 text-blue-700',
      cessato: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={classes[status] || 'bg-slate-100 text-slate-700'}>{statusLabels[status] || status}</Badge>;
  };

  const handleMarkPaid = async (billing, generateNext = true) => {
    try {
      const response = await axios.post(`${API}/telecom/billing/${billing.id}/mark-paid?generate_next=${generateNext}`);
      if (response.data.next_billing_generated) {
        toast.success('Fattura pagata - Prossima fattura generata automaticamente');
      } else {
        toast.success('Fattura marcata come pagata');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleSendEmail = async (billing) => {
    try {
      const response = await axios.post(`${API}/telecom/billing/${billing.id}/send-email`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const res = await axios.get(`${API}/telecom/email-config`);
      setEmailConfig(res.data);
    } catch (e) {}
  };

  const saveEmailConfig = async () => {
    setConfigLoading(true);
    try {
      await axios.put(`${API}/telecom/email-config`, emailConfig);
      toast.success('Configurazione salvata');
      setEmailConfigOpen(false);
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setConfigLoading(false);
    }
  };

  const toggleAmountSort = () => {
    setDateSort('none');
    setDueDateSort('none');
    setStatusSort('none');
    setAmountSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  const toggleDateSort = () => {
    setAmountSort('none');
    setDueDateSort('none');
    setStatusSort('none');
    setDateSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  const toggleDueDateSort = () => {
    setAmountSort('none');
    setDateSort('none');
    setStatusSort('none');
    setStatusSort('none');
    setDueDateSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  const toggleStatusSort = () => {
    setAmountSort('none');
    setDateSort('none');
    setDueDateSort('none');
    setStatusSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  // Filter clients for search in form
  const filteredClients = clients.filter(client => {
    if (!clientSearch) return true;
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    return fullName.includes(clientSearch.toLowerCase());
  });

  const filteredBillings = billings
    .filter(b => {
      const matchesSearch = `${b.client_name || ''} ${b.contract_number || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || b.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (amountSort === 'asc') return (a.amount || 0) - (b.amount || 0);
      if (amountSort === 'desc') return (b.amount || 0) - (a.amount || 0);
      if (dateSort === 'asc') return new Date(a.billing_date || 0) - new Date(b.billing_date || 0);
      if (dateSort === 'desc') return new Date(b.billing_date || 0) - new Date(a.billing_date || 0);
      if (dueDateSort === 'asc') return new Date(a.due_date || 0) - new Date(b.due_date || 0);
      if (dueDateSort === 'desc') return new Date(b.due_date || 0) - new Date(a.due_date || 0);
      if (statusSort === 'asc') return (a.status || '').localeCompare(b.status || '');
      if (statusSort === 'desc') return (b.status || '').localeCompare(a.status || '');
      return 0;
    });

  const clientContracts = contracts.filter(c => c.client_id === formData.client_id);

  const totalDaPagare = billings.filter(b => b.status === 'da_pagare').reduce((s, b) => s + (b.amount || 0), 0);
  const totalPagato = billings.filter(b => b.status === 'pagato').reduce((s, b) => s + (b.amount || 0), 0);

  const exportToExcel = () => {
    const headers = ['Cliente', 'Operatore', 'Data Fattura', 'Scadenza', 'Importo', 'Metodo Pagamento', 'Stato', 'Note'];
    const rows = filteredBillings.map(b => [
      b.client_name || '',
      b.contract_number || '',
      b.billing_date ? new Date(b.billing_date).toLocaleDateString('it-IT') : '',
      b.due_date ? new Date(b.due_date).toLocaleDateString('it-IT') : '',
      (b.amount || 0).toFixed(2).replace('.', ','),
      paymentMethods[b.payment_method] || b.payment_method || '',
      statusLabels[b.status] || b.status,
      b.notes || ''
    ]);
    const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fatture_telecom_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Esportazione completata');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="telecom-billing-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Fatturazione</h1>
          <p className="text-slate-600 mt-1">Gestisci le fatture dei contratti telefonici</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEmailConfigOpen(true)} data-testid="email-config-btn">
            <Settings className="w-4 h-4 mr-2" />Configurazione Email
          </Button>
          <Button variant="outline" onClick={exportToExcel} disabled={billings.length === 0} data-testid="export-billing-btn">
            <Download className="w-4 h-4 mr-2" />Esporta Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { setEditingBilling(null); setFormData(emptyBilling); setClientSearch(''); }} data-testid="add-billing-btn">
                <Plus className="w-4 h-4 mr-2" />Nuova Fattura
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBilling ? 'Modifica Fattura' : 'Nuova Fattura'}</DialogTitle>
              <DialogDescription>Inserisci i dati della fattura</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Input
                  placeholder="Cerca cliente per nome o cognome..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="mb-2"
                />
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v, contract_id: '' })}>
                  <SelectTrigger data-testid="billing-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    {filteredClients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contratto</Label>
                <Select value={formData.contract_id} onValueChange={(v) => setFormData({ ...formData, contract_id: v })} disabled={!formData.client_id}>
                  <SelectTrigger data-testid="billing-contract-select">
                    <SelectValue placeholder={formData.client_id ? "Seleziona contratto" : "Prima seleziona un cliente"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clientContracts.map((c) => (<SelectItem key={c.id} value={c.id}>{c.operator || 'Contratto'} - {c.contract_type}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo (€)</Label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" data-testid="billing-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Stato</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger data-testid="billing-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Fattura</Label>
                  <DateInput value={formData.billing_date} onChange={(val) => setFormData({ ...formData, billing_date: val })} data-testid="billing-date" placeholder="gg/mm/aaaa" />
                </div>
                <div className="space-y-2">
                  <Label>Scadenza</Label>
                  <DateInput value={formData.due_date} onChange={(val) => setFormData({ ...formData, due_date: val })} data-testid="billing-due-date" placeholder="gg/mm/aaaa" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Metodo Pagamento</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger data-testid="billing-payment-select"><SelectValue placeholder="Seleziona metodo" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(paymentMethods).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="billing-notes" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setClientSearch(''); }}>Annulla</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-billing-btn">
                  {editingBilling ? 'Salva Modifiche' : 'Crea Fattura'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Da Pagare</p>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalDaPagare)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Pagato</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalPagato)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Totale Fatture</p>
            <p className="text-2xl font-bold text-slate-900">{billings.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca per cliente, operatore..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-billing" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Billing Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filteredBillings.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm || filterStatus !== 'all' ? 'Nessuna fattura trovata' : 'Nessuna fattura registrata'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Operatore</TableHead>
                    <TableHead>
                      <button 
                        onClick={toggleDateSort}
                        className="inline-flex items-center gap-1 hover:text-violet-600 transition-colors font-medium"
                      >
                        Data
                        {dateSort === 'none' && <ArrowUpDown className="w-4 h-4 opacity-50" />}
                        {dateSort === 'asc' && <ArrowUp className="w-4 h-4 text-violet-600" />}
                        {dateSort === 'desc' && <ArrowDown className="w-4 h-4 text-violet-600" />}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button 
                        onClick={toggleDueDateSort}
                        className="inline-flex items-center gap-1 hover:text-violet-600 transition-colors font-medium"
                      >
                        Scadenza
                        {dueDateSort === 'none' && <ArrowUpDown className="w-4 h-4 opacity-50" />}
                        {dueDateSort === 'asc' && <ArrowUp className="w-4 h-4 text-violet-600" />}
                        {dueDateSort === 'desc' && <ArrowDown className="w-4 h-4 text-violet-600" />}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button 
                        onClick={toggleAmountSort}
                        className="inline-flex items-center gap-1 hover:text-violet-600 transition-colors font-medium"
                      >
                        Importo
                        {amountSort === 'none' && <ArrowUpDown className="w-4 h-4 opacity-50" />}
                        {amountSort === 'asc' && <ArrowUp className="w-4 h-4 text-violet-600" />}
                        {amountSort === 'desc' && <ArrowDown className="w-4 h-4 text-violet-600" />}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button 
                        onClick={toggleStatusSort}
                        className="inline-flex items-center gap-1 hover:text-violet-600 transition-colors font-medium"
                      >
                        Stato
                        {statusSort === 'none' && <ArrowUpDown className="w-4 h-4 opacity-50" />}
                        {statusSort === 'asc' && <ArrowUp className="w-4 h-4 text-violet-600" />}
                        {statusSort === 'desc' && <ArrowDown className="w-4 h-4 text-violet-600" />}
                      </button>
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBillings.map((billing) => (
                    <TableRow key={billing.id} data-testid={`billing-row-${billing.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-violet-600" />
                          </div>
                          <button 
                            className="font-medium text-left hover:text-violet-600 hover:underline transition-colors"
                            onClick={() => openClientDetails(billing.client_id)}
                            data-testid={`client-link-${billing.id}`}
                          >
                            {billing.client_name || '-'}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>{billing.contract_number || '-'}</TableCell>
                      <TableCell>{formatDate(billing.billing_date)}</TableCell>
                      <TableCell>{formatDate(billing.due_date)}</TableCell>
                      <TableCell className="font-medium text-violet-600">{formatCurrency(billing.amount)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {billing.status === 'da_pagare' ? (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => handleMarkPaid(billing)}
                              data-testid={`mark-paid-${billing.id}`}
                            >
                              ✓ Pagato
                            </Button>
                          ) : (
                            getStatusBadge(billing.status)
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`billing-menu-${billing.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {billing.status === 'da_pagare' && (
                              <>
                                <DropdownMenuItem onClick={() => handleMarkPaid(billing, true)}>
                                  <Receipt className="w-4 h-4 mr-2" />Pagato + Genera Prossima
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleMarkPaid(billing, false)}>
                                  <Receipt className="w-4 h-4 mr-2" />Solo Pagato
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => handleSendEmail(billing)}>
                              <Send className="w-4 h-4 mr-2" />Invia Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleClone(billing)}>
                              <Copy className="w-4 h-4 mr-2" />Clona
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(billing)}>
                              <Pencil className="w-4 h-4 mr-2" />Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(billing.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Details Dialog */}
      <Dialog open={clientDetailsOpen} onOpenChange={setClientDetailsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-violet-600" />
              Dettagli Cliente
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="space-y-4 mt-4">
              {!editingClient ? (
                <>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">{selectedClient.first_name} {selectedClient.last_name}</p>
                        <p className="text-sm text-slate-500">{selectedClient.client_code}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-2 pt-2 border-t">
                      {selectedClient.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span>{selectedClient.email}</span>
                        </div>
                      )}
                      {selectedClient.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span>{selectedClient.phone}</span>
                        </div>
                      )}
                      {(selectedClient.address || selectedClient.city) && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{[selectedClient.address, selectedClient.city, selectedClient.postal_code].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setClientDetailsOpen(false)}>Chiudi</Button>
                    <Button onClick={() => setEditingClient(true)} className="bg-violet-600 hover:bg-violet-700">
                      <Pencil className="w-4 h-4 mr-2" />Modifica
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nome</Label>
                        <Input 
                          value={clientFormData.first_name} 
                          onChange={(e) => setClientFormData({...clientFormData, first_name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cognome</Label>
                        <Input 
                          value={clientFormData.last_name} 
                          onChange={(e) => setClientFormData({...clientFormData, last_name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input 
                          value={clientFormData.email} 
                          onChange={(e) => setClientFormData({...clientFormData, email: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefono</Label>
                        <Input 
                          value={clientFormData.phone} 
                          onChange={(e) => setClientFormData({...clientFormData, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Indirizzo</Label>
                      <Input 
                        value={clientFormData.address} 
                        onChange={(e) => setClientFormData({...clientFormData, address: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Città</Label>
                        <Input 
                          value={clientFormData.city} 
                          onChange={(e) => setClientFormData({...clientFormData, city: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CAP</Label>
                        <Input 
                          value={clientFormData.postal_code} 
                          onChange={(e) => setClientFormData({...clientFormData, postal_code: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setEditingClient(false)}>Annulla</Button>
                    <Button onClick={handleSaveClient} className="bg-emerald-600 hover:bg-emerald-700">
                      Salva Modifiche
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Config Dialog */}
      <Dialog open={emailConfigOpen} onOpenChange={setEmailConfigOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurazione Automazione Email Telefonia
            </DialogTitle>
            <DialogDescription>
              Configura i parametri per l'invio automatico delle email di promemoria scadenza contratti telefonici.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Automazione Attiva</Label>
                <p className="text-sm text-slate-500">Abilita l'invio automatico giornaliero delle email</p>
              </div>
              <Switch
                checked={emailConfig.enabled}
                onCheckedChange={(checked) => setEmailConfig({...emailConfig, enabled: checked})}
                data-testid="telecom-email-enabled"
              />
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Orario di Invio Giornaliero
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">L'invio automatico verrà eseguito ogni giorno a questo orario (fuso orario: Europe/Rome)</p>
                </div>
                <Input
                  type="time"
                  value={emailConfig.daily_send_time || "09:00"}
                  onChange={(e) => setEmailConfig({...emailConfig, daily_send_time: e.target.value})}
                  className="w-32 text-center font-mono text-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email Mittente</Label>
                <Input value={emailConfig.sender_email} disabled className="bg-slate-100" />
                <p className="text-xs text-slate-500">Forzato: no-reply@jvservice.it</p>
              </div>
              <div className="space-y-2">
                <Label>Nome Mittente</Label>
                <Input
                  value={emailConfig.sender_name}
                  onChange={(e) => setEmailConfig({...emailConfig, sender_name: e.target.value})}
                  placeholder="JV Service - Ufficio Telefonia"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-medium">Giorni Prima della Scadenza</Label>
              <p className="text-sm text-slate-500">Configura quanti giorni prima della scadenza contratto inviare la notifica</p>
              <div className="max-w-xs">
                <div className="space-y-2 p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-violet-600" />
                    <Label>Contratti Telefonici</Label>
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    value={emailConfig.days_before_expiry}
                    onChange={(e) => setEmailConfig({...emailConfig, days_before_expiry: parseInt(e.target.value) || 30})}
                  />
                  <p className="text-xs text-slate-500">giorni prima</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                <Label className="text-base font-medium">Template Email</Label>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Oggetto Email <span className="text-xs font-normal text-slate-500">(supporta variabili)</span>
                </Label>
                <Input
                  value={emailConfig.email_subject}
                  onChange={(e) => setEmailConfig({...emailConfig, email_subject: e.target.value})}
                  placeholder="Scadenza Contratto - {operator} - JV Service"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Corpo del Messaggio <span className="text-xs font-normal text-slate-500">(personalizzabile)</span>
                </Label>
                <Textarea
                  value={emailConfig.email_body}
                  onChange={(e) => setEmailConfig({...emailConfig, email_body: e.target.value})}
                  placeholder="Gentile {client_name},&#10;&#10;le ricordiamo che il suo contratto {contract_type} con {operator} scadrà il giorno {expiry_date}."
                  rows={10}
                  className="font-mono text-sm leading-relaxed"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border">
                <p className="text-sm font-medium text-slate-700 mb-2">Variabili disponibili:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{client_name}'}</code>
                    <span className="text-slate-500">Nome cliente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{operator}'}</code>
                    <span className="text-slate-500">Operatore telefonico</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{contract_type}'}</code>
                    <span className="text-slate-500">Tipo contratto</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{expiry_date}'}</code>
                    <span className="text-slate-500">Data scadenza</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{days_remaining}'}</code>
                    <span className="text-slate-500">Giorni rimanenti</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{phone_number}'}</code>
                    <span className="text-slate-500">Numero in servizio</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Firma Email (inclusa automaticamente):</p>
                <div className="text-sm text-slate-600 space-y-1">
                  <p className="font-semibold text-slate-800">JV SERVICE SRLS</p>
                  <p>Via Fabio Filzi, 39 L, 59100 Prato (PO) – Italy</p>
                  <p>Cell: +39 375 675 2552</p>
                  <p>Email: operatore@tlcne.it</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEmailConfigOpen(false)}>Annulla</Button>
            <Button onClick={saveEmailConfig} disabled={configLoading}>
              {configLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Salva Configurazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TelecomBilling;
